import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { siftApiClient, type UserSession } from '../../api/siftApiClient';
import { ThemeToggle } from '../theme/ThemeToggle';
import { legacyAuthEnabled } from '../../auth/msalConfig';
import ChangePasswordModal from './ChangePasswordModal';
import './UserMenu.css';

interface UserMenuProps {
  user: UserSession;
  onLogout: () => void;
  onOpenAdmin?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ user, onLogout, onOpenAdmin }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Position dropdown when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      // Align dropdown's right edge with trigger's right edge
      // For fixed positioning, right = distance from viewport right edge
      const rightOffset = document.documentElement.clientWidth - rect.right;
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        right: rightOffset,
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedDropdown = dropdownRef.current?.contains(target);
      
      if (!clickedTrigger && !clickedDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('pointerdown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await siftApiClient.logout();
      onLogout();
    } catch (err) {
      console.error('Logout failed:', err);
      onLogout(); // Still clear local state
    }
  };

  const getRoleBadgeClass = (role: string) => {
    if (role === 'admin') return 'role-admin';
    if (role === 'NATIONAL') return 'role-national';
    return 'role-region';
  };

  const dropdown = isOpen ? createPortal(
    <div 
      ref={dropdownRef}
      className="user-menu-dropdown glass-card"
      style={dropdownStyle}
    >
      <div className="user-menu-header">
        <div className="user-email glass-text-secondary">{user.email}</div>
      </div>
      
      <div className="user-menu-items">
        <div className="menu-item theme-item">
          <ThemeToggle />
          <span className="menu-item-text">Toggle Theme</span>
        </div>
        {legacyAuthEnabled && (
          <button
            className="glass-button"
            onClick={() => { setShowChangePassword(true); setIsOpen(false); }}
          >
            🔑 Change Password
          </button>
        )}
        {onOpenAdmin && (
          <button 
            className="glass-button"
            onClick={() => { onOpenAdmin(); setIsOpen(false); }}
          >
            ⚙️ Admin Panel
          </button>
        )}
        <button className="glass-button logout-button" onClick={handleLogout}>
          🚪 Sign Out
        </button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="user-menu">
      <button 
        ref={triggerRef}
        className="user-menu-trigger glass-button" 
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="user-avatar">
          {(user.display_name || user.email)[0].toUpperCase()}
        </span>
        <span className="user-name glass-text-primary">
          {user.display_name || user.email.split('@')[0]}
        </span>
        <span className={`user-role ${getRoleBadgeClass(user.role)}`}>{user.role}</span>
      </button>
      {dropdown}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default UserMenu;
